/// <amd-module name="Widgets/CodeScalarWidget/CodeScalarWidget" />
import * as React from "react";

import { VssComponent } from "VSS/Platform/Layout";
import * as VssUserClaims from "VSS/Platform/UserClaims";

import { Scalar } from "Reporting/Visuals/Scalar";
import { UnconfiguredMessage } from "Reporting/Visuals/VisualMessages/UnconfiguredMessage";

import { CodeScalarWidgetDataProvider } from "Widgets/CodeScalarWidget/CodeScalarWidgetDataProvider";
import * as Resources from "Widgets/CodeScalarWidget/Resources";

import { ITrackName } from "Dashboards/WidgetCommon/ITrackName";
import * as WidgetTelemetry from "Dashboards/WidgetCommon/WidgetTelemetry";
import { LoadingStatus, WidgetContextMenuColorMode, WidgetProps } from "Dashboards/WidgetContracts/WidgetProps";

import { IVCNavigationService } from "VC/Common/Navigation";

import * as TfsWidgetContext from "Tfs/Widget/Context";

export interface ICodeScalarConfiguration extends ITrackName {
    repositoryId: string;
    version: string;
    path: string;
}

export interface ICodeScalarWidgetState {
    commitsCount: number;
}

export class CodeScalarWidget extends VssComponent<WidgetProps, ICodeScalarWidgetState> {
    public static readonly tfvcFilesRoute = "ms.vss-code-web.tfvc-files-route";
    public static readonly gitFilesRoute = "ms.vss-code-web.files-route-git";

    constructor(props: WidgetProps) {
        super(props);

        this.state = {
            commitsCount: -1 // -1 means that we didn't load the count as yet
        };

        this.props.setMenuColorMode(WidgetContextMenuColorMode.Dark);
    }

    public async componentDidMount() {
        this.fetchAndSetCommitsCount();
        WidgetTelemetry.onWidgetLoaded(this.context.pageContext, this.props);
    }

    public async componentDidUpdate(prevProps: WidgetProps) {
        if (prevProps.settings != this.props.settings) {
            this.fetchAndSetCommitsCount();
        }
    }

    public render(): JSX.Element {
        if (!this.props.settings) {
            return <UnconfiguredMessage visualTitle={this.props.name} size={this.props.size} onClick={this.props.onConfigure} />;
        }

        return (
            <Scalar
                backgroundColor={"#68217a"} // this is the purple color which is currently undefined in any scss in the new platform
                subtitle={Resources.Subtitle}
                title={this.props.name}
                value={this.state.commitsCount}
                maxDisplayValue={99} // The changesets REST API is restricted to returning a max of 100
                targetUrl={this.getRepositoryUrl()}
                onClick={() => WidgetTelemetry.onWidgetClick(this.context.pageContext, this.props)}
            />
        );
    }

    private async fetchAndSetCommitsCount() {
        const configuration = this.parseSettings();

        if (!configuration) {
            this.props.onWidgetLoaded();
            return;
        }

        if (configuration) {
            const dataProvider = new CodeScalarWidgetDataProvider(this.context.pageContext);
            try {
                const commitsCount = await dataProvider.getData(
                    this.getProjectName(),
                    configuration.repositoryId,
                    configuration.version,
                    configuration.path
                );
                this.setState({ commitsCount: commitsCount }, this.props.onWidgetLoaded);
            } catch (error) {
                this.props.onWidgetLoaded({ outcome: LoadingStatus.failure, outcomeDescription: error });
            }
        }
    }

    private getRepositoryUrl(): string | undefined {
        const configuration = this.parseSettings();

        const isUserMember = VssUserClaims.userHasClaim(this.context.pageContext, VssUserClaims.UserClaims.Member);
        if (!configuration || !isUserMember) {
            return undefined;
        }

        // URL needs project, path and version for git , just project and path for TFVC
        const navigationService = this.context.pageContext.getService<IVCNavigationService>("IVCNavigationService");

        const routeId = configuration.version ? CodeScalarWidget.gitFilesRoute : CodeScalarWidget.tfvcFilesRoute;

        const params: { [key: string]: string } = {
            path: configuration.path,
            _a: "history"
        };

        if (configuration.version) {
            params.version = configuration.version;
        }

        const url = navigationService.getRepositoryUrl(
            configuration.repositoryId,
            this.getProjectName(), // projectName
            undefined, // teamName
            routeId,
            params
        );
        return url;
    }

    private parseSettings(): ICodeScalarConfiguration | null {
        try {
            return this.props.settings !== null && JSON.parse(this.props.settings);
        } catch (e) {
            // Express non-parsable/invalid configurations as null setting
            return null;
        }
    }

    private getProjectName(): string {
        return TfsWidgetContext.getProject(this.context.pageContext).name;
    }
}

VssComponent.register("codeScalarWidget", CodeScalarWidget);
